import axios from 'axios';
import { API_BASE_URL, API_AUTH_URL, buildApiUrl, buildStorageUrl } from '../config/api';
import analyticsService from './analyticsService';

// Configuração do axios com a URL base dinâmica
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Interceptor para adicionar token de autenticação automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag para evitar loop infinito de refresh
let isRefreshing = false;
let failedRequestsQueue = [];

const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedRequestsQueue = [];
};

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    // Se o backend enviou um novo token no header (refresh automático), salvar
    const newToken = response.headers['authorization'];
    if (newToken && newToken.startsWith('Bearer ')) {
      const token = newToken.slice(7);
      localStorage.setItem('token', token);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user) {
        user.access_token = token;
        localStorage.setItem('user', JSON.stringify(user));
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Evita tentar refresh em requisições de login/register/refresh
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/quick-start') ||
      originalRequest?.url?.includes('/auth/google') ||
      originalRequest?.url?.includes('/course-preview') ||
      originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Enfileira a requisição enquanto o refresh está em andamento
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        isRefreshing = false;
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${API_AUTH_URL}/refresh`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );
        const newToken = response.data.access_token;
        localStorage.setItem('token', newToken);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user) {
          user.access_token = newToken;
          localStorage.setItem('user', JSON.stringify(user));
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        processQueue(null, newToken);
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (!axios.isCancel(error)) {
      const responseMessage = error.response?.data?.message || error.response?.data?.error;
      analyticsService.trackClientError({
        status: error.response?.status,
        endpoint: originalRequest?.url,
        method: originalRequest?.method,
        message: typeof responseMessage === 'string' ? responseMessage : error.message,
      });
    }

    return Promise.reject(error);
  }
);

// Serviços de API organizados
export const apiService = {
  // === CURSOS ===
  cursos: {
    // Listar cursos com paginação e filtros
    list: (params = {}) => {
      const query = new URLSearchParams();
      if (params.page) query.set('page', params.page);
      if (params.per_page) query.set('per_page', params.per_page);
      if (params.categoria) query.set('categoria', params.categoria);
      const qs = query.toString();
      return api.get(`/cursos${qs ? `?${qs}` : ''}`);
    },
    
    // Obter curso específico
    get: (id) => api.get(`/cursos/${id}`),

    // Resolve public course/category slugs and legacy slugs.
    resolveSlug: (slug) => api.get(`/catalogo/${encodeURIComponent(slug)}`),
    
    // Criar novo curso
    create: (courseData) => api.post('/admin/cursos', courseData),
    
    // Atualizar curso
    update: (id, courseData) => api.put(`/admin/cursos/${id}`, courseData),
    
    // Deletar curso
    delete: (id) => api.delete(`/admin/cursos/${id}`),
    
    // Matricular em curso
    enroll: (courseData) => api.post('/enroll', courseData),
    
    // Upload de imagem do curso
    uploadImagem: (cursoId, imageData) => {
      const payload = typeof imageData === 'string' 
        ? { imagem: imageData }
        : imageData;
      return api.post(`/admin/cursos/${cursoId}/imagem`, payload);
    },
    
    // Remover imagem do curso
    removerImagem: (cursoId) => api.delete(`/admin/cursos/${cursoId}/imagem`),

    // Obter prova do curso (questões e opções)
    getProva: (id) => api.get(`/cursos/${id}/prova`),
  },

  // === MÓDULOS ===
  modulos: {
    // Listar módulos de um curso
    listByCourse: (cursoId) => api.get(`/cursos/${cursoId}/modulos`),
    
    // Obter módulo específico
    get: (cursoId, moduloId) => api.get(`/cursos/${cursoId}/modulos/${moduloId}`),
    
    // Criar novo módulo
    create: (cursoId, moduloData) => api.post(`/admin/cursos/${cursoId}/modulos`, moduloData),
    
    // Atualizar módulo
    update: (cursoId, moduloId, data) => api.put(`/admin/cursos/${cursoId}/modulos/${moduloId}`, data),
    
    // Deletar módulo
    delete: (cursoId, moduloId) => api.delete(`/admin/cursos/${cursoId}/modulos/${moduloId}`),
  },

  // === EXPERIÊNCIA DO CURSO (SEM CONTA) ===
  preview: {
    start: (cursoId, moduloId) => api.post('/course-preview', {
      curso_id: Number(cursoId),
      modulo_id: Number(moduloId),
    }),
    getModule: (cursoId, moduloId, previewToken) => api.get(
      `/course-preview/${cursoId}/modules/${moduloId}`,
      { headers: { 'X-Course-Preview': previewToken } }
    ),
  },

  // === USUÁRIO ===
  user: {
    // Obter dados do usuário
    get: () => api.get('/user'),
    
    // Atualizar dados do usuário
    update: (userData) => api.post('/user', userData),
    
    // Obter certificados do usuário
    certificates: () => api.get('/meus-certificados'),
  },

  // === AUTENTICAÇÃO ===
  auth: {
    // Login
    login: (credentials) => axios.post(`${API_AUTH_URL}/login`, credentials),
    
    // Registro
    register: (userData) => axios.post(`${API_AUTH_URL}/register`, userData),

    // Cadastro ou acesso rápido durante o curso
    quickStart: (userData) => axios.post(`${API_AUTH_URL}/quick-start`, userData),

    // Login ou cadastro com token de identidade verificado pelo backend
    google: (credential, context = {}) => axios.post(`${API_AUTH_URL}/google`, {
      credential,
      ...context,
    }),
    
    // Logout
    logout: () => api.post('/logout'),
    
    // Verificar token
    verify: () => api.get('/verify'),
  },

  // === CATEGORIAS ===
  categorias: {
    list: () => api.get('/categorias'),
    get: (id) => api.get(`/categorias/${id}`),
    getBySlug: (slug) => api.get(`/categorias/slug/${slug}`),
  },

  // === BUSCA ===
  search: (q, config = {}) => api.get(`/search?q=${encodeURIComponent(q)}`, config),

  // === PROGRESSO ===
  progress: {
    // Obter progresso do curso
    getCourse: (cursoId) => api.get(`/cursos/${cursoId}/progress`),
    
    // Atualizar progresso
    update: (data) => api.post('/progress', data),
  },

  // === CONTATO ===
  contact: {
    send: (data) => api.post('/contato', data),
  },

  // === CURRÍCULO PROFISSIONAL ===
  resumes: {
    access: () => api.get('/resumes/access'),
    list: () => api.get('/resumes'),
    create: (data = {}) => api.post('/resumes', data),
    get: (id) => api.get(`/resumes/${id}`),
    update: (id, data) => api.put(`/resumes/${id}`, data),
    delete: (id) => api.delete(`/resumes/${id}`),
    duplicate: (id) => api.post(`/resumes/${id}/duplicate`),
    makePrimary: (id) => api.post(`/resumes/${id}/primary`),
    suggestSummary: (data) => api.post('/resumes/suggest-summary', data),
    download: (id, format) => api.get(`/resumes/${id}/${format}`, {
      responseType: 'blob',
      timeout: 60000,
    }),
  },

  // === ADMIN ===
  admin: {
    dashboard: () => api.get('/admin/dashboard'),
    analytics: (days = 30, page = '') => {
      const query = new URLSearchParams({ days: String(days) });
      if (page) query.set('page', page);
      return api.get(`/admin/analytics?${query.toString()}`);
    },
    usageInsights: (days = 30) => api.get(`/admin/usage-insights?days=${encodeURIComponent(days)}`),
    users: (q = '', page = 1, perPage = 20) => {
      const query = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      if (q) query.set('q', q);
      return api.get(`/admin/users?${query.toString()}`);
    },
    user: (id) => api.get(`/admin/users/${id}`),
    updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
    userResume: (userId, resumeId) => api.get(`/admin/users/${userId}/resumes/${resumeId}`),
    downloadUserResume: (userId, resumeId) => api.get(
      `/admin/users/${userId}/resumes/${resumeId}/pdf`,
      { responseType: 'blob', timeout: 60000 }
    ),
    downloadUserCertificate: (userId, courseId) => api.get(
      `/admin/users/${userId}/certificates/${courseId}/pdf`,
      { responseType: 'blob', timeout: 60000 }
    ),
    sendCertificateReminder: (paymentId) => api.post(`/admin/payments/${paymentId}/certificate-reminder`),
    sendCourseContinuationReminder: (enrollmentId) => api.post(`/admin/enrollments/${enrollmentId}/course-reminder`),
  }
};

// Helper para URLs de storage/imagens
export const getStorageUrl = (path) => {
  if (!path) return '';
  return buildStorageUrl(path);
};

// Helper para chamadas diretas (para compatibilidade)
export const makeApiCall = (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  return axios(url, options);
};

export default api;
